import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { modKeyLabel } from '@/utils/platform';
import { useIsMobile } from '@/hooks/useMediaQuery';

const mod = modKeyLabel();

export function KeyboardLegend() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(false);

  const shortcuts = [
    { key: 'Tab', desc: t('keyboard.addChild') },
    { key: 'Enter', desc: t('keyboard.addSibling') },
    { key: 'Space / F2', desc: t('keyboard.editNode') },
    { key: 'Delete', desc: t('keyboard.deleteNode') },
    { key: 'Arrows', desc: t('keyboard.navigate') },
    { key: '/', desc: t('keyboard.collapseExpand') },
    { key: `${mod}+Z`, desc: t('keyboard.undo') },
    { key: `${mod}+C/X/V`, desc: t('keyboard.copyCutPaste') },
    { key: `${mod}+F`, desc: t('keyboard.searchShortcut') },
    { key: `${mod}++/-`, desc: t('keyboard.zoomInOut') },
    { key: `${mod}+0`, desc: t('keyboard.fitToScreen') },
    { key: 'N', desc: t('keyboard.notesShortcut') },
    { key: 'A', desc: t('keyboard.attachmentsShortcut') },
    { key: 'I', desc: t('keyboard.aiAssist') },
    { key: 'Shift+C', desc: t('keyboard.commentsShortcut') },
    { key: 'Alt+Drag', desc: t('keyboard.freePosition') },
    { key: '?', desc: t('keyboard.allShortcuts') },
  ];

  if (isMobile) return null;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 36,
        left: 12,
        zIndex: 10,
        fontFamily: 'var(--font-family)',
        userSelect: 'none',
      }}
    >
      <button
        onClick={() => setCollapsed((v) => !v)}
        style={{
          background: 'var(--bg-app)',
          color: 'var(--text-tertiary)',
          border: '1px solid var(--border-subtle)',
          borderRadius: collapsed ? 6 : '6px 6px 0 0',
          padding: '4px 10px',
          fontSize: 'var(--font-size-xs)',
          cursor: 'pointer',
          fontFamily: 'var(--font-family)',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <span style={{ fontSize: 'var(--font-size-sm)' }}>{'\u2328'}</span>
        {t('keyboard.shortcuts')}
        <span style={{ fontSize: 9, marginLeft: 2 }}>{collapsed ? '\u25B2' : '\u25BC'}</span>
      </button>

      {!collapsed && (
        <div
          style={{
            background: 'var(--bg-app)',
            border: '1px solid var(--border-subtle)',
            borderTop: 'none',
            borderRadius: '0 6px 6px 6px',
            padding: '6px 0',
            minWidth: 180,
          }}
        >
          {shortcuts.map((s) => (
            <div
              key={s.key}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '2px 10px',
                gap: 12,
              }}
            >
              <kbd
                style={{
                  background: 'var(--bg-surface)',
                  color: '#cbd5e1',
                  border: '1px solid var(--border-default)',
                  borderRadius: 3,
                  padding: '1px 5px',
                  fontSize: 10,
                  fontFamily: 'var(--font-family)',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  width: 70,
                  minWidth: 70,
                  textAlign: 'center',
                  display: 'inline-block',
                  boxSizing: 'border-box',
                }}
              >
                {s.key}
              </kbd>
              <span
                style={{
                  color: 'var(--text-tertiary)',
                  fontSize: 10,
                  flex: 1,
                  textAlign: 'right',
                }}
              >
                {s.desc}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
